export type Placement = "top" | "bottom" | "left" | "right" | "center";

export type TourStep = {
  /** CSS selector for the element to highlight. Null = center-of-screen narrative card. */
  selector: string | null;
  /** Optional short label shown above the heading (e.g. "REFINEMENTS"). */
  eyebrow?: string;
  heading: string;
  body: string;
  placement?: Placement;
  /** ms to dwell on this step before auto-advancing. */
  dwellMs?: number;
};

export const tourSteps: TourStep[] = [
  {
    selector: ".appbar-brand",
    eyebrow: "WELCOME",
    heading: "This is Affinage",
    body: "A slow, deliberate cookbook for the recipes you actually cook — kept, refined, and remembered over time. Let me show you around.",
    placement: "bottom",
    dwellMs: 7500,
  },
  {
    selector: '[data-tour-id="nav-cookbook"]',
    eyebrow: "THE SHELF",
    heading: "Your cookbook",
    body: "Everything you write, generate, or fork lives here. Think of this page as the bookshelf — every recipe you'll ever keep is on it.",
    placement: "bottom",
    dwellMs: 8000,
  },
  {
    selector: '[data-tour-id="recipe-card-first"]',
    eyebrow: "OPEN IT",
    heading: "Open a recipe",
    body: "Click any card to open the full page — hero photo, ingredients, steps, macros, and notes. Everything you need to actually cook from it.",
    placement: "right",
    dwellMs: 8500,
  },
  {
    selector: null,
    eyebrow: "INSIDE A RECIPE",
    heading: "Scale, edit, taste, note",
    body: "On a recipe page you can scale servings up or down (the ingredient amounts follow), swap or rewrite ingredients, edit steps, and jot tasting notes. Nothing is precious — change anything.",
    placement: "center",
    dwellMs: 10000,
  },
  {
    selector: null,
    eyebrow: "REFINEMENTS",
    heading: "Every change is kept",
    body: "Every time you save an edit, Affinage stores it as a new revision — like git history for your kitchen. More salt last time? Longer simmer? A swapped pan? It's all there. You can compare any two versions side by side, and roll back if a change didn't pan out.",
    placement: "center",
    dwellMs: 12000,
  },
  {
    selector: null,
    eyebrow: "MILESTONES",
    heading: "Mark the keepers",
    body: "When a revision is the one — the version your family asked for seconds of — mark it as a milestone and give it a name. Future-you will thank you when you can't quite remember what was different about that one Sunday roast.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: '[data-tour-id="nav-ondeck"]',
    eyebrow: "PLAN",
    heading: "Put a week on the board",
    body: "On Deck is the week ahead — breakfast, lunch, dinner and snacks, laid out by the day. Send a recipe there from its own page and spread its servings across as many days as it'll actually feed you.",
    placement: "bottom",
    dwellMs: 9500,
  },
  {
    selector: '[data-tour-id="nav-shopping"]',
    eyebrow: "SHOP",
    heading: "The list writes itself",
    body: "Everything on the board adds up into one shopping list, sorted by aisle, with the same ingredient from three recipes totalled into a single line. You never write it — you just read it.",
    placement: "bottom",
    dwellMs: 9500,
  },
  {
    selector: null,
    eyebrow: "IN THE SHOP",
    heading: "Take it with you",
    body: "On an iPhone the list goes to Reminders — which means it shows up in the Grocery app, one list per week, made when the week has something to buy and taken away when it doesn't. Tick something off in the aisle and it ticks off here too.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: null,
    eyebrow: "COOK",
    heading: "Cook from your phone",
    body: "Cooking mode is one step at a time, big enough to read from across the counter, with the screen kept awake and timers running in the background. Swipe forward as you go. Walk away mid-recipe and it remembers where you were.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: null,
    eyebrow: "LOG",
    heading: "Tick what you actually ate",
    body: "Plans change, so nothing is recorded until you say it happened. Tick a meal as eaten and its calories and macros go to Apple Health, where the food diary you already use can pick them up. Untick it and it comes back out.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: '[data-tour-id="new-recipe"]',
    eyebrow: "ADD YOUR OWN",
    heading: "Add your own",
    body: "Write a recipe by hand — paste one a friend sent, type one from a cookbook, or import from a URL. It's yours from the start.",
    placement: "bottom",
    dwellMs: 8000,
  },
  {
    selector: '.chat-trigger, [data-tour-id="chat-trigger-mobile"]',
    eyebrow: "OR DRAFT IT",
    heading: "Or have it drafted",
    body: "Tell the AI what you're craving — 'a weeknight pasta with what's in my fridge' — and it'll produce a full recipe you can save, cook, and refine like any other.",
    placement: "bottom",
    dwellMs: 9000,
  },
  {
    selector: null,
    eyebrow: "ITERATE WITH AI",
    heading: "Refine in plain English",
    body: "Once a recipe exists, the assistant can revise it for you. \"Make this vegetarian.\" \"Cut the sugar by a third.\" \"Scale for eight.\" Each AI revision becomes just another revision in the history — fully editable, fully reversible.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: '[data-tour-id="nav-community"]',
    eyebrow: "COMMUNITY",
    heading: "The Kitchen Table",
    body: "A feed of recipes other Affinage cooks have published. No ranked algorithm, no ads — just dishes people actually cook.",
    placement: "bottom",
    dwellMs: 8000,
  },
  {
    selector: null,
    eyebrow: "FORKING",
    heading: "Make it yours",
    body: "Fork any community recipe with one click and it lands in your cookbook. From there it's yours — refine it, scale it, mark milestones, change it until it's the version your family asks for. The original cook still gets credit at the root.",
    placement: "center",
    dwellMs: 11000,
  },
  {
    selector: '[data-tour-id="nav-assistant"]',
    eyebrow: "ANYTIME",
    heading: "The Assistant",
    body: "And the assistant is always one tap away — for substitutions, ratios, timing, ingredient swaps, or just a sanity check before you start cooking.",
    placement: "bottom",
    dwellMs: 8000,
  },
  {
    selector: null,
    eyebrow: "ALL SET",
    heading: "Go tend something",
    body: "Your cookbook stays empty until you fill it — write one, generate one, or fork one from the Kitchen Table. You can replay this tour anytime from the top bar.",
    placement: "center",
    dwellMs: 9000,
  },
];
